import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/config/env';
import { getAuthTokenProvider } from './authTokenProvider';
import { toApiError } from './apiErrorHandler';

const REQUEST_TIMEOUT_MS = 30_000;

/** Marks a request that has already been retried so a failing refresh cannot loop. */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _hasRetriedAfterRefresh?: boolean;
}

/**
 * Concurrent 401s share one refresh call, across every client. Without this, a page issuing
 * several requests would fire a refresh per request and invalidate its own new tokens.
 */
let refreshInFlight: Promise<string> | null = null;

function refreshAccessTokenOnce(): Promise<string> {
  refreshInFlight ??= getAuthTokenProvider()
    .refreshAccessToken()
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export interface ApiClientOptions {
  /** Send cookies with each request. */
  readonly withCredentials?: boolean;
  /**
   * Whether a 401 that survives a token refresh ends the session. True for Core and
   * Identity, where it can only mean the session is gone. A service that also answers 401
   * for its own reasons sets this to false, so its failure is shown instead.
   */
  readonly endSessionOnUnauthorized?: boolean;
}

export class ApiClient {
  private readonly instance: AxiosInstance;
  private readonly endSessionOnUnauthorized: boolean;

  constructor(
    baseURL: string,
    { withCredentials = false, endSessionOnUnauthorized = true }: ApiClientOptions = {}
  ) {
    this.endSessionOnUnauthorized = endSessionOnUnauthorized;
    this.instance = axios.create({
      baseURL,
      timeout: REQUEST_TIMEOUT_MS,
      withCredentials,
      headers: { 'Content-Type': 'application/json' },
    });

    this.attachRequestInterceptor();
    this.attachResponseInterceptor();
  }

  private attachRequestInterceptor(): void {
    this.instance.interceptors.request.use((request) => {
      const token = getAuthTokenProvider().getAccessToken();
      if (token !== null) {
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    });
  }

  private attachResponseInterceptor(): void {
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        const apiError = toApiError(error);

        if (apiError.kind !== 'unauthorized' || !axios.isAxiosError(error)) {
          throw apiError;
        }

        const original = error.config as RetryableRequestConfig | undefined;
        if (original === undefined || original._hasRetriedAfterRefresh === true) {
          if (this.endSessionOnUnauthorized) getAuthTokenProvider().onSessionExpired();
          throw apiError;
        }

        original._hasRetriedAfterRefresh = true;

        // Only a failed refresh means the session is gone. The retry is outside this catch:
        // its own failure (a 500, or a 401 handled above) is that request's, not the session's.
        let freshToken: string;
        try {
          freshToken = await refreshAccessTokenOnce();
        } catch (refreshError) {
          getAuthTokenProvider().onSessionExpired();
          throw toApiError(refreshError);
        }
        original.headers.Authorization = `Bearer ${freshToken}`;
        return this.instance.request(original);
      }
    );
  }

  async get<T>(url: string, requestConfig?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, requestConfig);
    return response.data;
  }

  async post<T>(url: string, body?: unknown, requestConfig?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, body, requestConfig);
    return response.data;
  }

  async put<T>(url: string, body?: unknown, requestConfig?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, body, requestConfig);
    return response.data;
  }

  async patch<T>(url: string, body?: unknown, requestConfig?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, body, requestConfig);
    return response.data;
  }

  async delete<T>(url: string, requestConfig?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, requestConfig);
    return response.data;
  }

  async postBlob(url: string, body?: unknown, requestConfig?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.instance.post<Blob>(url, body, {
      ...requestConfig,
      responseType: 'blob',
    });
    return response.data;
  }

  /** Escape hatch for endpoints that stream files. */
  async getBlob(url: string, requestConfig?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.instance.get<Blob>(url, {
      ...requestConfig,
      responseType: 'blob',
    });
    return response.data;
  }
}

export const apiClient = new ApiClient(config.apiBaseUrl);

/** Authenticated Identity API calls use its HttpOnly session cookie and the access token. */
export const identityApiClient = new ApiClient(config.authBaseUrl, { withCredentials: true });

/**
 * Conbent.Notifications: what the signed-in user follows. Same bearer token as Core, but it
 * also answers 401 when it cannot check a token (IdentityServer unreachable, say), which must
 * not sign the user out of the whole app. Unused when `isNotificationsEnabled` is false.
 */
export const notificationsApiClient = new ApiClient(config.notificationsBaseUrl ?? '', {
  endSessionOnUnauthorized: false,
});

/** Login/refresh bootstrap the session, so they cannot depend on an existing bearer token. */
export const authApiClient = axios.create({
  baseURL: config.authBaseUrl,
  timeout: REQUEST_TIMEOUT_MS,
  withCredentials: true,
});
